<template>
  <v-card class="kanban-column h-100" flat>
    <!-- Column Header -->
    <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
      <div class="d-flex align-center">
        <v-icon :color="color" size="18" class="me-2">
          {{ columnIcon }}
        </v-icon>
        <h3 class="text-h6 font-weight-medium">{{ title }}</h3>
        <v-chip :color="color" size="small" variant="tonal" class="ml-2">
          {{ activities.length }}
        </v-chip>
      </div>

      <!-- Column Menu -->
      <v-menu v-if="authStore.isAdmin()">
        <template #activator="{ props }">
          <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="small" />
        </template>
        <v-list density="compact">
          <v-list-item @click="handleAddActivity">
            <template #prepend>
              <v-icon>mdi-plus</v-icon>
            </template>
            <v-list-item-title>Adicionar Atividade</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-divider />

    <!-- Column Content -->
    <v-card-text class="pa-3 kanban-column-content">

      <div v-if="loading" class="text-center pa-8" style="height:100vh">
        <v-skeleton-loader type="card" class="mb-5"></v-skeleton-loader>
        <v-skeleton-loader type="card" class="mb-5"></v-skeleton-loader>
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </div>

      <!-- Empty State -->
      <div v-else-if="!activities.length" class="empty-state text-center pa-4">
        <v-icon :color="color" size="48" class="mb-2 opacity-50">
          {{ emptyStateIcon }}
        </v-icon>
        <p class="text-body-2 text-medium-emphasis mb-3">
          Nenhuma atividade {{ emptyStateText }}
        </p>
      </div>

      <!-- Activities List -->
      <div v-else class="activities-list">
        <KanbanCard v-for="activity in sortedActivities" :key="activity.id" :activity="activity"
          @view="handleViewActivity" @click="handleEditActivity(activity.id)" @edit="handleEditActivity(activity.id)"
          @view-history="handleViewHistory" @view-subtask-history="handleViewSubtaskHistory"
          @duplicate="handleDuplicateActivity(activity.id)" @delete="handleDeleteActivity(activity.id)"
          @add-subtask="$emit('addSubtask', $event)" @edit-subtask="handleEditSubtask($event)"
          @delete-subtask="handleDeleteSubtask($event)" @update-subtask-status="handleUpdateSubtaskStatus($event)"
          @update-activity-status="handleUpdateActivityStatus($event)" />
      </div>

      <!-- Add Activity Button (Bottom) -->
      <div v-if="!loading && authStore.isAdmin()" class="add-activity-bottom mt-3">
        <v-btn :color="color" variant="outlined" block size="small" prepend-icon="mdi-plus" @click="handleAddActivity">
          Adicionar Atividade
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import KanbanCard from './KanbanCard.vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

const props = defineProps({
  title: String,
  status: String,
  color: String,
  activities: {
    type: Array,
    default: () => []
  },
  loading: Boolean
})

const emit = defineEmits([
  'activityMoved',
  'addActivity',
  'viewActivity',
  'viewHistory',
  'editActivity',
  'deleteActivity',
  'duplicateActivity',
  'addSubtask',
  'editSubtask',
  'viewSubtaskHistory',
  'deleteSubtask',
  'updateSubtaskStatus',
  'updateActivityStatus'
])

const isDragOver = ref(false)

const sortedActivities = computed(() => {
  if (!props.activities || !Array.isArray(props.activities)) {
    return []
  }

  return [...props.activities].sort((a, b) => {
    const orderA = a.ordem || a.order || '0'
    const orderB = b.ordem || b.order || '0'

    const partsA = orderA.replace(/\.$/, '').split('.').map(n => parseInt(n) || 0)
    const partsB = orderB.replace(/\.$/, '').split('.').map(n => parseInt(n) || 0)

    const maxLength = Math.max(partsA.length, partsB.length)
    for (let i = 0; i < maxLength; i++) {
      const partA = partsA[i] || 0
      const partB = partsB[i] || 0

      if (partA !== partB) {
        return partA - partB
      }
    }

    return 0
  })
})

const columnIcon = computed(() => {
  const icons = {
    'not_started': 'mdi-circle-outline',
    'in_progress': 'mdi-clock-outline',
    'completed': 'mdi-check-circle'
  }
  return icons[props.status] || 'mdi-circle-outline'
})

const emptyStateIcon = computed(() => {
  const icons = {
    'not_started': 'mdi-plus-circle-outline',
    'in_progress': 'mdi-clock-outline',
    'completed': 'mdi-check-circle-outline'
  }
  return icons[props.status] || 'mdi-plus-circle-outline'
})

const emptyStateText = computed(() => {
  const texts = {
    'not_started': 'não iniciadas',
    'in_progress': 'em andamento',
    'completed': 'concluídas'
  }
  return texts[props.status] || ''
})

const handleAddActivity = () => {
  emit('addActivity', props.status)
}

const handleViewHistory = (activityId) => {
  emit('viewHistory', activityId)
}

const handleViewSubtaskHistory = (subtaskId) => {
  emit('viewSubtaskHistory', subtaskId)
}

const handleEditActivity = (activityId) => {
  emit('editActivity', activityId)
}

const handleDuplicateActivity = (activityId) => {
  emit('duplicateActivity', activityId)
}

const handleDeleteActivity = (activityId) => {
  emit('deleteActivity', activityId)
}

const handleEditSubtask = (subtaskId) => {
  emit('editSubtask', subtaskId)
}

const handleDeleteSubtask = (subtaskId) => {
  emit('deleteSubtask', subtaskId)
}

const handleViewActivity = (activityId) => {
  emit('viewActivity', activityId)
}

const handleUpdateSubtaskStatus = (data) => {
  emit('updateSubtaskStatus', data)
}

const handleUpdateActivityStatus = (data) => {
  emit('updateActivityStatus', data)
}

</script>

<style scoped>
.kanban-column {
  background-color: rgb(var(--v-theme-surface-container-low));
  border: 1px solid rgb(var(--v-theme-outline-variant));
  border-radius: 8px;
}

.kanban-column-content {
  height: calc(100% - 64px);
  overflow-y: auto;
  min-height: 400px;
}

.activities-list {
  min-height: 200px;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.add-activity-bottom {
  position: sticky;
  bottom: 0;
  background-color: rgb(var(--v-theme-surface-container-low));
  padding-top: 8px;
  margin-top: auto;
}

/* Drag and Drop styles */
.drag-over {
  background-color: rgba(var(--v-theme-primary), 0.1);
  border-color: rgb(var(--v-theme-primary));
}

/* Scrollbar personalizada */
.kanban-column-content::-webkit-scrollbar {
  width: 6px;
}

.kanban-column-content::-webkit-scrollbar-track {
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 3px;
}

.kanban-column-content::-webkit-scrollbar-thumb {
  background: rgb(var(--v-theme-outline));
  border-radius: 3px;
}

.kanban-column-content::-webkit-scrollbar-thumb:hover {
  background: rgb(var(--v-theme-outline-variant));
}

/* Responsividade */
@media (max-width: 960px) {
  .kanban-column {
    margin-bottom: 16px;
  }

  .kanban-column-content {
    min-height: 300px;
  }

  .activities-list {
    min-height: 150px;
  }

  .empty-state {
    min-height: 150px;
  }
}

@media (max-width: 600px) {
  .kanban-column-content {
    min-height: 250px;
    padding: 12px;
  }

  .activities-list {
    min-height: 100px;
  }

  .empty-state {
    min-height: 100px;
    padding: 16px;
  }

  .add-activity-bottom {
    padding-top: 6px;
  }
}
</style>
